// #767 Story F — per-user open-threads admin view.
//
// Lists every `open_threads` row for the user, active first
// (still hydrate-eligible, will surface in the dealer's next
// hand) then resolved (completed / declined / expired). Each row
// shows: topic_summary, detection_class, opened_at, resolved_at,
// outcome (badge), context_snippet (admin-only — see privacy
// contract in `services/cards/open_threads.py`), and a link to
// the conversation where the thread was opened.
//
// Bas asked for this on staging 2026-05-17: the runtime Dealer
// hand view only shows the next recall card, not the full
// history of what the post-call detector has been picking up.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminFetch } from './userSubPageStyles';

const VERSION = 'v1.0.0';

const DETECTION_COLORS = {
  user_signaled:             { bg: '#E8F5E9', fg: '#2E7D32' }, // high confidence — green
  activity_cut_short:        { bg: '#FFF8E1', fg: '#795548' }, // medium — amber
  reminiscence_trailing_off: { bg: '#F3E5F5', fg: '#6A1B9A' }, // low — purple
};

const OUTCOME_COLORS = {
  completed: { bg: '#E8F5E9', fg: '#2E7D32' },
  declined:  { bg: '#FFEBEE', fg: '#C62828' },
  expired:   { bg: '#ECEFF1', fg: '#546E7A' },
};

function badge(text, palette) {
  const c = palette[text] || { bg: '#EEE', fg: '#666' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px', borderRadius: 10,
      fontSize: '0.72rem', fontWeight: 600,
      background: c.bg, color: c.fg,
      textTransform: 'capitalize',
    }}>{text.replace(/_/g, ' ')}</span>
  );
}

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ThreadRow({ row, isActive }) {
  return (
    <tr style={{ borderBottom: '1px solid #f3f3f3', verticalAlign: 'top' }}>
      <td style={{ padding: '10px 6px', maxWidth: 320 }}>
        <div style={{ fontSize: '0.92rem', fontWeight: 500 }}>
          {row.topic_summary}
        </div>
        {row.context_snippet && (
          <div style={{
            marginTop: 6, padding: '6px 8px',
            background: '#FAFAFA', borderLeft: '3px solid #ddd',
            fontSize: '0.78rem', color: '#666', fontStyle: 'italic',
          }}>
            “{row.context_snippet}”
            <div style={{
              fontSize: '0.68rem', color: '#aaa', marginTop: 2,
              fontStyle: 'normal',
            }}>
              context snippet — admin-only (verbatim user quote)
            </div>
          </div>
        )}
      </td>
      <td style={{ padding: '10px 6px' }}>
        {badge(row.detection_class, DETECTION_COLORS)}
      </td>
      <td style={{ padding: '10px 6px', fontSize: '0.82rem', color: '#666' }}>
        {fmtTs(row.opened_at)}
      </td>
      <td style={{ padding: '10px 6px', fontSize: '0.82rem' }}>
        {isActive
          ? <span style={{ color: '#2E7D32' }}>active</span>
          : (row.outcome
              ? badge(row.outcome, OUTCOME_COLORS)
              : <span style={{ color: '#aaa' }}>—</span>)
        }
      </td>
      <td style={{ padding: '10px 6px', fontSize: '0.82rem', color: '#666' }}>
        {isActive ? '—' : fmtTs(row.resolved_at)}
      </td>
      <td style={{ padding: '10px 6px', fontSize: '0.82rem' }}>
        {row.conversation_id && (
          <Link to={`/admin/conversations/${row.conversation_id}`}>
            view call →
          </Link>
        )}
      </td>
    </tr>
  );
}

function ThreadTable({ rows, isActive }) {
  if (!rows || rows.length === 0) {
    return (
      <p className="empty-state" style={{ marginTop: 8 }}>
        {isActive
          ? 'No active open threads. Either nothing pending, or all recent threads have been picked up + resolved.'
          : 'No resolved threads yet.'}
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem',
      }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Topic</th>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Detected as</th>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Opened</th>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Outcome</th>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Resolved</th>
            <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Origin call</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <ThreadRow key={r.id} row={r} isActive={isActive} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminUserOpenThreads() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminFetch(`/api/admin/users/${id}/open-threads`);
        if (cancelled) return;
        setData(r || { active: [], resolved: [] });
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <Link to={`/admin/users/${id}`} style={{ fontSize: '0.85rem' }}>
          ← back to user
        </Link>
        <h2 style={{ marginTop: 8 }}>
          🧵 Open threads <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.78rem' }}>{VERSION}</span>
        </h2>
        <p style={{ color: '#666', fontSize: '0.88rem' }}>
          Conversational arcs the user left unfinished — the dealer surfaces
          active ones as <code>talk_open_thread:&lt;id&gt;</code> cards with PICK UP
          framing on the next call. Locked v0: 14-day window; declined arcs
          never re-deal; completed arcs (dealt + not declined) stop surfacing.
        </p>
        {error && <p className="error-state">Error: {error}</p>}

        {data && (
          <>
            <h3 style={{ marginTop: 28 }}>
              Active <span style={{ color: '#666', fontSize: '0.78rem', fontWeight: 400 }}>
                ({data.active.length})
              </span>
            </h3>
            <ThreadTable rows={data.active} isActive={true} />

            <h3 style={{ marginTop: 28 }}>
              Resolved <span style={{ color: '#666', fontSize: '0.78rem', fontWeight: 400 }}>
                ({data.resolved.length})
              </span>
            </h3>
            <ThreadTable rows={data.resolved} isActive={false} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
